/**
 * A deploy replaces the hashed JS chunks and deletes the old ones, so a tab
 * opened before the deploy fails the next time it lazy-loads code. Reloading
 * fetches the new index.html and with it the new chunk names.
 *
 * The esbuild `application` builder loads chunks with native `import()`, so
 * the failure is a browser error, not webpack's `ChunkLoadError`.
 */
const STALE_CHUNK_MESSAGES = [
  /failed to fetch dynamically imported module/i, // Chrome, Edge
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /failed to load module script/i, // HTML served in place of a missing chunk
  /loading chunk \S+ failed/i // webpack builds
];

const RELOAD_KEY = 'chunk_reload_at';
/** Never reload more than once in this window, so a genuinely broken chunk cannot loop. */
const RELOAD_GUARD_MS = 10_000;

export function isStaleChunkError(error: unknown): boolean {
  const err = error as { name?: string; message?: string } | null;
  if (err?.name === 'ChunkLoadError') {
    return true;
  }
  const message = err?.message ?? String(error ?? '');
  return STALE_CHUNK_MESSAGES.some(pattern => pattern.test(message));
}

/**
 * Reload once, at `url` when given (the navigation that failed) or the
 * current page. Returns false when a reload already happened recently.
 */
export function reloadForStaleChunk(url?: string): boolean {
  try {
    const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - lastReload <= RELOAD_GUARD_MS) {
      return false;
    }
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Without storage there is no loop guard, and a reload loop is worse than one failed navigation.
    return false;
  }
  if (url) {
    window.location.href = url;
  } else {
    window.location.reload();
  }
  return true;
}

/** Call after a successful navigation so a later deploy can trigger a reload again. */
export function clearStaleChunkReload(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // ignore storage errors
  }
}
